/**
 * Requirement amount and effectiveness helpers.
 * Fortnight overlap rules match backend Requirement.is_effective_on_date semantics
 * applied across a fortnight window (start through end inclusive).
 */

/** Parse YYYY-MM-DD (or ISO) as local calendar date (no timezone drift). */
export const parseDateOnly = (dateStr) => {
  if (!dateStr) return null;
  const part = String(dateStr).split('T')[0];
  const [y, m, d] = part.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

/** End date of a fortnight given its start (13 days after start). */
export const fortnightEndFromStart = (fortnightStart) => {
  const end = new Date(fortnightStart);
  end.setDate(end.getDate() + 13);
  return end;
};

/**
 * True if the requirement contributes to this fortnight's required amount.
 * Overlap: effective_from <= fortnight_end AND (no until OR until >= fortnight_start).
 */
export const isRequirementEffectiveForFortnight = (req, fortnightStart, fortnightEnd) => {
  try {
    const from = parseDateOnly(req.effective_from_date);
    const until = parseDateOnly(req.effective_until_date);
    if (from && from > fortnightEnd) return false;
    if (until && until < fortnightStart) return false;
    return true;
  } catch (e) {
    return true;
  }
};

/** @returns {'active' | 'not_yet' | 'ended'} */
export const getRequirementFortnightStatus = (req, fortnightStart, fortnightEnd) => {
  if (isRequirementEffectiveForFortnight(req, fortnightStart, fortnightEnd)) {
    return 'active';
  }
  const from = parseDateOnly(req.effective_from_date);
  if (from && from > fortnightEnd) return 'not_yet';
  return 'ended';
};

/** Single-point check (legacy / today). */
export const isRequirementEffectiveNow = (req, referenceDate = new Date()) => {
  try {
    const checkDate = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
    if (req.effective_from_date) {
      const fromDate = parseDateOnly(req.effective_from_date);
      if (fromDate && checkDate < fromDate) return false;
    }
    if (req.effective_until_date) {
      const untilDate = parseDateOnly(req.effective_until_date);
      if (untilDate && checkDate > untilDate) return false;
    }
    return true;
  } catch (e) {
    return true;
  }
};

/** Fortnightly dollar contribution for one requirement (canonical formula). */
export const calculateFortnightlyAmount = (req) => {
  const amount = parseFloat(req.amount);
  if (isNaN(amount)) return 0;

  switch (req.frequency) {
    case 'weekly':
      return amount * 2;
    case 'fortnightly':
      return amount;
    case 'monthly':
      return amount * (12 / 26);
    case 'quarterly':
      return amount * (4 / 26);
    case 'yearly':
      return amount / 26;
    case 'custom': {
      const days = parseInt(req.custom_days, 10);
      if (!days || days <= 0) return amount;
      if (days <= 14) return amount;
      return amount * (14 / days);
    }
    default:
      return 0;
  }
};

/**
 * Sum periodic totals for requirements.
 * @param {Array} requirements
 * @param {boolean} activeOnly
 * @param {{ fortnightStart?: Date, fortnightEnd?: Date }} [options]
 */
export const calculatePeriodTotals = (requirements, activeOnly = true, options = {}) => {
  const { fortnightStart, fortnightEnd } = options;
  const useFortnight = fortnightStart && fortnightEnd;

  return requirements.reduce(
    (totals, req) => {
      if (activeOnly) {
        const effective = useFortnight
          ? isRequirementEffectiveForFortnight(req, fortnightStart, fortnightEnd)
          : isRequirementEffectiveNow(req);
        if (!effective) return totals;
      }

      const amount = parseFloat(req.amount);
      if (isNaN(amount)) return totals;

      switch (req.frequency) {
        case 'weekly':
          totals.weekly += amount;
          totals.fortnightly += amount * 2;
          totals.quarterly += amount * 13;
          totals.yearly += amount * 52;
          break;
        case 'fortnightly':
          totals.weekly += amount / 2;
          totals.fortnightly += amount;
          totals.quarterly += amount * 6.5;
          totals.yearly += amount * 26;
          break;
        case 'monthly':
          totals.weekly += amount * (12 / 52);
          totals.fortnightly += amount * (12 / 26);
          totals.quarterly += amount * 3;
          totals.yearly += amount * 12;
          break;
        case 'quarterly':
          totals.weekly += amount * (4 / 52);
          totals.fortnightly += amount * (4 / 26);
          totals.quarterly += amount;
          totals.yearly += amount * 4;
          break;
        case 'yearly':
          totals.weekly += amount / 52;
          totals.fortnightly += amount / 26;
          totals.quarterly += amount / 4;
          totals.yearly += amount;
          break;
        default:
          break;
      }

      return totals;
    },
    { weekly: 0, fortnightly: 0, quarterly: 0, yearly: 0 }
  );
};

/** Display kind labels for UI */
export const REQUIREMENT_DISPLAY_KINDS = {
  allocation: 'Allocation',
  bill_estimated: 'Bill (estimated)',
  bill_actual: 'Bill (actual)',
};

const isEffectiveOnDate = (req, checkDate) => {
  if (!isRequirementEffectiveNow(req, checkDate)) return false;
  return true;
};

/**
 * V2 funding model: minimum allocation + flags.
 * Mirrors requirements/calculation.py.
 */
export const computeAccountRequiredFortnightly = (
  requirements,
  fortnightStart,
  fortnightEnd,
  referenceDate = null
) => {
  const checkDate = referenceDate || fortnightStart;
  const effective = requirements.filter((req) =>
    fortnightStart && fortnightEnd
      ? isRequirementEffectiveForFortnight(req, fortnightStart, fortnightEnd)
      : isEffectiveOnDate(req, checkDate)
  );

  const baseReq = effective.find((r) => r.is_account_base);
  const others = effective.filter(
    (r) =>
      !r.is_account_base &&
      !r.exclude_from_required_amount &&
      !r.additive_to_total
  );
  const additive = effective.filter(
    (r) => r.additive_to_total && !r.exclude_from_required_amount
  );

  const othersSum = others.reduce((s, r) => s + calculateFortnightlyAmount(r), 0);
  const baseTarget = baseReq ? calculateFortnightlyAmount(baseReq) : 0;
  const core = baseReq ? Math.max(baseTarget, othersSum) : othersSum;
  const additiveSum = additive.reduce((s, r) => s + calculateFortnightlyAmount(r), 0);

  return core + additiveSum;
};

/**
 * Per-account funding breakdown for UI (mirrors computeAccountRequiredFortnightly).
 * All amounts are fortnightly dollars for the viewed fortnight.
 */
export const explainAccountFunding = (requirements, fortnightStart, fortnightEnd) => {
  const effective = requirements.filter((req) =>
    isRequirementEffectiveForFortnight(req, fortnightStart, fortnightEnd)
  );

  const baseReq = effective.find((r) => r.is_account_base);
  const others = effective.filter(
    (r) =>
      !r.is_account_base &&
      !r.exclude_from_required_amount &&
      !r.additive_to_total
  );
  const additive = effective.filter(
    (r) => r.additive_to_total && !r.exclude_from_required_amount
  );
  const excluded = effective.filter((r) => r.exclude_from_required_amount);

  const othersSum = others.reduce((s, r) => s + calculateFortnightlyAmount(r), 0);
  const baseTarget = baseReq ? calculateFortnightlyAmount(baseReq) : 0;
  const core = baseReq ? Math.max(baseTarget, othersSum) : othersSum;
  const additiveSum = additive.reduce((s, r) => s + calculateFortnightlyAmount(r), 0);
  const total = core + additiveSum;

  return {
    hasBase: Boolean(baseReq),
    baseTarget,
    othersSum,
    core,
    additiveSum,
    total,
    excludedCount: excluded.length,
  };
};

/** Sum required amounts across all bank accounts in a requirement list. */
export const sumRequiredFortnightlyForRequirements = (
  requirements,
  fortnightStart,
  fortnightEnd
) => {
  const byAccount = {};
  requirements.forEach((req) => {
    const key = req.bank_account ?? 'none';
    if (!byAccount[key]) byAccount[key] = [];
    byAccount[key].push(req);
  });

  return Object.entries(byAccount).reduce((sum, [key, reqs]) => {
    if (key === 'none') {
      return (
        sum +
        reqs.reduce((s, r) => {
          if (!isRequirementEffectiveForFortnight(r, fortnightStart, fortnightEnd)) return s;
          if (r.exclude_from_required_amount) return s;
          return s + calculateFortnightlyAmount(r);
        }, 0)
      );
    }
    return sum + computeAccountRequiredFortnightly(reqs, fortnightStart, fortnightEnd);
  }, 0);
};

/** Sum fortnightly contributions for active requirements in the viewed fortnight. */
export const sumActiveFortnightlyForFortnight = (requirements, fortnightStart, fortnightEnd) => {
  const grouped = groupRequirementsForDisplay(requirements, fortnightStart, fortnightEnd);
  return sumRequiredFortnightlyForRequirements(grouped, fortnightStart, fortnightEnd);
};

/** Per bank account: { [accountId]: { nickname, clientTotal } } */
export const totalsByBankAccountForFortnight = (requirements, fortnightStart, fortnightEnd) => {
  const map = {};
  requirements.forEach((req) => {
    if (!req.bank_account) return;
    const id = req.bank_account;
    if (!map[id]) {
      map[id] = {
        nickname: req.bank_account_details?.nickname || `Account ${id}`,
        reqs: [],
      };
    }
    map[id].reqs.push(req);
  });
  Object.keys(map).forEach((id) => {
    map[id].clientTotal = computeAccountRequiredFortnightly(
      map[id].reqs,
      fortnightStart,
      fortnightEnd
    );
    delete map[id].reqs;
  });
  return map;
};

/** Pick one segment per series for list display in the viewed fortnight. */
export const pickDisplaySegment = (segments, fortnightStart, fortnightEnd) => {
  if (!segments.length) return null;

  const overlaps = (req) => isRequirementEffectiveForFortnight(req, fortnightStart, fortnightEnd);

  const active = segments.filter(overlaps);
  if (active.length) {
    return active.sort((a, b) => {
      const aFrom = parseDateOnly(a.effective_from_date)?.getTime() || 0;
      const bFrom = parseDateOnly(b.effective_from_date)?.getTime() || 0;
      return aFrom - bFrom || a.id - b.id;
    })[active.length - 1];
  }

  const future = segments.filter((req) => {
    const from = parseDateOnly(req.effective_from_date);
    return from && from > fortnightEnd;
  });
  if (future.length) {
    return future.sort((a, b) => {
      const aFrom = parseDateOnly(a.effective_from_date)?.getTime() || 0;
      const bFrom = parseDateOnly(b.effective_from_date)?.getTime() || 0;
      return aFrom - bFrom || a.id - b.id;
    })[0];
  }

  const past = segments.filter((req) => {
    const until = parseDateOnly(req.effective_until_date);
    return until && until < fortnightStart;
  });
  if (past.length) {
    return past.sort((a, b) => {
      const aUntil = parseDateOnly(a.effective_until_date)?.getTime() || 0;
      const bUntil = parseDateOnly(b.effective_until_date)?.getTime() || 0;
      return aUntil - bUntil || a.id - b.id;
    })[past.length - 1];
  }

  return segments[0];
};

/** One row per requirement series for list/timeline display. */
export const groupRequirementsForDisplay = (requirements, fortnightStart, fortnightEnd) => {
  if (!fortnightStart || !fortnightEnd) return requirements;

  const bySeries = {};
  requirements.forEach((req) => {
    const key = req.bill_series_id || `id-${req.id}`;
    if (!bySeries[key]) bySeries[key] = [];
    bySeries[key].push(req);
  });

  return Object.values(bySeries)
    .map((segments) => pickDisplaySegment(segments, fortnightStart, fortnightEnd))
    .filter(Boolean);
};

export const formatEffectiveRange = (req) => {
  const from = req.effective_from_date ? formatDateShort(req.effective_from_date) : '—';
  const until = req.effective_until_date ? formatDateShort(req.effective_until_date) : 'Ongoing';
  return `${from} → ${until}`;
};

const formatDateShort = (dateStr) => {
  const d = parseDateOnly(dateStr);
  if (!d) return dateStr;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};
